import { html } from './env';
import { lenis } from './scroll';

/*
  Menu mobile plein écran (partials/header.twig) :
  [data-menu] (.is-open), [data-menu-toggle], liens .menu__link, html.menu-open.

  Menu ouvert, le reste de la page est inerte et le focus clavier boucle entre
  le burger (qui sert de bouton de fermeture) et le contenu du menu.
*/

const menuEl = () => document.querySelector<HTMLElement>('[data-menu]');
const burgerEl = () => document.querySelector<HTMLButtonElement>('[data-menu-toggle]');

/** Rend inertes (ou non) les enfants du body qui ne contiennent ni le menu ni le burger. */
function setBackgroundInert(inert: boolean, menu: HTMLElement, burger: HTMLElement) {
  for (const element of document.body.children) {
    if (element === menu || element.contains(burger) || element.tagName === 'SCRIPT') continue;
    element.toggleAttribute('inert', inert);
  }
}

function trapFocus(event: KeyboardEvent, menu: HTMLElement, burger: HTMLElement) {
  const focusable = [burger, ...menu.querySelectorAll<HTMLElement>('a[href], button')].filter(
    (element) => element.offsetParent !== null,
  );
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function setMenu(open: boolean, { restoreFocus = true } = {}) {
  const menu = menuEl();
  const burger = burgerEl();
  if (!menu || !burger) return;
  if (menu.classList.contains('is-open') === open) return;

  menu.classList.toggle('is-open', open);
  html.classList.toggle('menu-open', open);
  menu.toggleAttribute('inert', !open);
  setBackgroundInert(open, menu, burger);
  menu.setAttribute('aria-hidden', String(!open));
  burger.setAttribute('aria-expanded', String(open));
  burger.setAttribute('aria-label', open ? 'Close the menu' : 'Open the menu');

  if (open) {
    lenis?.stop();
    menu.scrollTop = 0;
    menu.querySelector<HTMLElement>('.menu__link')?.focus({ preventScroll: true });
  } else {
    lenis?.start();
    if (restoreFocus) burger.focus({ preventScroll: true });
  }
}

export function initMenu() {
  document.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    if (target.closest('[data-menu-toggle]')) {
      setMenu(!menuEl()?.classList.contains('is-open'));
    } else if (target.closest('[data-menu] a')) {
      // fermer d'abord pour que Lenis puisse défiler (ou la page changer)
      setMenu(false, { restoreFocus: false });
    }
  });

  document.addEventListener('keydown', (event) => {
    const menu = menuEl();
    const burger = burgerEl();
    if (!menu?.classList.contains('is-open') || !burger) return;
    if (event.key === 'Escape') setMenu(false);
    else if (event.key === 'Tab') trapFocus(event, menu, burger);
  });

  // Retour en desktop (breakpoint `l:` de Tailwind) : rien ne reste ouvert
  matchMedia('(width >= 64rem)').addEventListener('change', (event) => {
    if (event.matches) setMenu(false, { restoreFocus: false });
  });
}
